import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner, Text, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import FeedItem from "components/custom/feedItem";
import DemoDataToggle from "components/custom/demoDataToggle";
import FollowRequests from "../FollowRequests";
import { useLazyGetFeedQuery } from "features/community/apis";
import { demoFeed } from "features/community/mockData/social";
import { COMMUNITY_PEOPLE } from "constants/app-paths";
import theme from "utils/themeVars";
import styles from "./Feed.module.less";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "quizzes", label: "Quizzes" },
  { key: "sessions", label: "Study Sessions" },
  { key: "scripts", label: "Scripts" },
  { key: "shared", label: "Recommendations" },
];

const EVENT_TO_FILTER = {
  quiz_completed: "quizzes",
  study_session_created: "sessions",
  study_session_completed: "sessions",
  script_favorited: "scripts",
  script_completed: "scripts",
  content_shared: "shared",
};

const Feed = () => {
  const navigate = useNavigate();
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const [type, setType] = useState(() => {
    if (typeof window === "undefined") return "all";
    return window.localStorage.getItem("illnessrx-feed-filter") || "all";
  });
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const typeRef = useRef(type);
  const loadMoreRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  typeRef.current = type;

  const [trigger, { isFetching }] = useLazyGetFeedQuery();

  useEffect(() => {
    window.localStorage.setItem("illnessrx-feed-filter", type);
  }, [type]);

  const load = useCallback(
    async (reset) => {
      if (!reset) setIsLoadingMore(true);
      try {
        const res = await trigger({
          type: type === "all" ? undefined : type,
          before: reset ? undefined : nextBefore,
        });
        const data = res?.data;
        if (data) {
          setItems((prev) => (reset ? data.data || [] : [...prev, ...(data.data || [])]));
          setNextBefore(data.nextBefore || null);
        }
      } finally {
        if (reset) setIsRefreshing(false);
        else {
          loadMoreInFlightRef.current = false;
          setIsLoadingMore(false);
        }
      }
    },
    [trigger, type, nextBefore]
  );

  useEffect(() => {
    setIsRefreshing(true);
    setItems([]);
    setNextBefore(null);
    load(true);
    // eslint-disable-next-line
  }, [type]);

  const displayedItems = useDemoData
    ? demoFeed.filter((item) => type === "all" || EVENT_TO_FILTER[item.type] === type)
    : items;
  const isUpdating = !useDemoData && (isRefreshing || (isFetching && !isLoadingMore));

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !nextBefore || isFetching || isRefreshing) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || loadMoreInFlightRef.current) return;
        loadMoreInFlightRef.current = true;
        load(false);
      },
      { rootMargin: "240px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextBefore, isFetching, isRefreshing, load]);

  // Live updates pushed from the WebSocket layer.
  useEffect(() => {
    const onNew = (e) => {
      const activity = e.detail;
      if (!activity) return;
      const currentType = typeRef.current;
      if (
        currentType !== "all" &&
        EVENT_TO_FILTER[activity.type] !== currentType
      ) {
        return;
      }
      setItems((prev) => {
        if (prev.some((i) => i.activity_event_id === activity.activity_event_id)) {
          return prev;
        }
        return [activity, ...prev];
      });
    };
    window.addEventListener("social:feed:new", onNew);
    return () => window.removeEventListener("social:feed:new", onNew);
  }, []);

  const renderBody = () => (
    <div className={styles.feedPage}>
      <FollowRequests />

      <div className={styles.feedHeader}>
        <div>
          <Title level={3} className={styles.feedTitle}>Activity feed</Title>
          <Text className={styles.feedDescription}>
            Keep up with quizzes, study sessions and recommended learning content.
          </Text>
        </div>
      </div>

      <div className={styles.filterBar}>
        <span className={styles.filterLabel}>Filter activity</span>
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="small"
            type={type === f.key ? "primary" : "light"}
            color={theme["primary-color"]}
            text={f.label}
            disabled={isUpdating}
            onClick={() => {
              if (f.key === type) return;
              setIsRefreshing(true);
              setType(f.key);
            }}
          />
        ))}
      </div>

      {isUpdating && displayedItems.length === 0 ? (
        <div className={styles.skeletonList} aria-label="Loading activity">
          {Array.from({ length: 4 }).map((_, index) => (
            <div className={styles.skeletonItem} key={index}>
              <span />
              <div><i /><i /><i /></div>
            </div>
          ))}
        </div>
      ) : displayedItems.length === 0 ? (
        <div className={styles.emptyState}>
          <Title level={4} className={styles.emptyTitle}>Your feed is empty</Title>
          <Text className="gray-800">
            Follow learners and educators to see their study activity and shared
            resources here.
          </Text>
          <Button size="small" type="default" text="Find people" icon="FiUsers" onClick={() => navigate(COMMUNITY_PEOPLE)} />
        </div>
      ) : (
        <div className={styles.feedCard}>
          {isUpdating && (
            <div className={styles.refreshOverlay} role="status" aria-label="Updating activity">
              <Spinner size="small" />
            </div>
          )}
          {displayedItems.map((item) => (
            <FeedItem key={item.activity_event_id} item={item} />
          ))}
        </div>
      )}

      {!useDemoData && nextBefore && (
        <div
          ref={loadMoreRef}
          className={isLoadingMore ? styles.scrollSentinelLoading : styles.scrollSentinel}
        >
          {isLoadingMore && <Spinner size="small" />}
        </div>
      )}
      {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Feed data" />}
    </div>
  );

  return (
    <PageLayout label={<Title level={2}>Community</Title>}>
      {renderBody()}
    </PageLayout>
  );
};

export default Feed;
